import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Upload, FileSpreadsheet, FileText, Paperclip, Trash2, Loader2, CheckCircle2, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { SheetPreview } from '../components/tributario/SheetPreview';
import { uploadInChunks, formatBytes, formatDateTime, estadoBadge, MES_LABEL } from '../components/tributario/tributarioUtils';
import { Formulario350 } from '../components/tributario/f350/Formulario350';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ESTADOS = ['Borrador', 'En elaboración', 'En revisión', 'Presentada', 'Archivada'];
const TIPOS_DOC = ['Balance de prueba', 'Estados financieros', 'Certificados', 'Soporte', 'Otro'];

const downloadBlob = async (url, filename) => {
  const res = await axios.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a'); a.href = href; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
};

export default function TributarioDeclaracion() {
  const { declId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [decl, setDecl] = useState(null);
  const [users, setUsers] = useState([]);
  const [progress, setProgress] = useState(null);
  const [docProgress, setDocProgress] = useState(null);
  const [tipoDoc, setTipoDoc] = useState('Balance de prueba');
  const [versionSel, setVersionSel] = useState(null);
  const [hojasSel, setHojasSel] = useState([]);
  const [preview, setPreview] = useState({ hoja: '', data: null, loading: false });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [modo, setModo] = useState('online');
  const excelRef = useRef(); const docRef = useRef();

  const load = useCallback(async () => {
    const { data } = await axios.get(`${BACKEND_URL}/api/tributario/declaraciones/${declId}`);
    setDecl(data);
    return data;
  }, [declId]);

  useEffect(() => {
    load().catch((e) => toast.error(e.response?.data?.detail || 'No se pudo cargar'));
    axios.get(`${BACKEND_URL}/api/users`).then((r) => setUsers(r.data)).catch(() => {});
  }, [load]);

  const procesando = decl?.versiones?.some((v) => v.parse_status === 'procesando');
  useEffect(() => {
    if (!procesando) return;
    const t = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [procesando, load]);

  useEffect(() => {
    if (!decl) return;
    const ultima = [...(decl.versiones || [])].reverse().find((v) => v.parse_status === 'listo');
    if (ultima && (!versionSel || !decl.versiones.find((v) => v.id === versionSel))) {
      setVersionSel(ultima.id);
      setHojasSel(decl.formulario_info?.hojas_formulario?.filter((h) => ultima.sheets.some((s) => s.name === h)) || []);
    }
  }, [decl, versionSel]);

  const update = async (patch) => {
    try { await axios.put(`${BACKEND_URL}/api/tributario/declaraciones/${declId}`, patch); await load(); toast.success('Guardado'); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const subirExcel = async (file) => {
    if (!file) return;
    if (!/\.(xlsm|xlsx)$/i.test(file.name)) { toast.error('Sube el archivo .xlsm diligenciado'); return; }
    setProgress(0);
    try {
      await uploadInChunks({ declId, file, kind: 'excel', onProgress: setProgress });
      toast.success('Excel cargado. Procesando hojas…');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al subir'); }
    finally { setProgress(null); if (excelRef.current) excelRef.current.value = ''; }
  };

  const subirDoc = async (files) => {
    for (const file of Array.from(files || [])) {
      setDocProgress(0);
      try { await uploadInChunks({ declId, file, kind: 'documento', tipoDoc, onProgress: setDocProgress }); toast.success(`${file.name} adjuntado`); }
      catch (e) { toast.error(e.response?.data?.detail || `Error al subir ${file.name}`); }
    }
    setDocProgress(null); if (docRef.current) docRef.current.value = '';
    await load();
  };

  const verHoja = async (nombre) => {
    setPreview({ hoja: nombre, data: null, loading: true });
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/versiones/${versionSel}/hoja`, { params: { nombre } });
      setPreview({ hoja: nombre, data, loading: false });
    } catch (e) { toast.error('No se pudo cargar la hoja'); setPreview({ hoja: '', data: null, loading: false }); }
  };

  const descargarPdf = async () => {
    if (!hojasSel.length) { toast.error('Selecciona al menos una hoja'); return; }
    setPdfLoading(true);
    try {
      await downloadBlob(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/versiones/${versionSel}/pdf?hojas=${encodeURIComponent(hojasSel.join('||'))}`, `${decl.formulario}_${decl.nit}_${decl.anio}.pdf`);
    } catch (e) { toast.error('No se pudo generar el PDF'); }
    finally { setPdfLoading(false); }
  };

  if (!decl) return <div className="p-8 text-sm text-slate-500">Cargando…</div>;
  const info = decl.formulario_info || {};
  const version = decl.versiones?.find((v) => v.id === versionSel);

  return (
    <div className="space-y-6" data-testid="tributario-declaracion-page">
      <button onClick={() => navigate('/tributario')} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="decl-volver"><ArrowLeft className="w-4 h-4" /> Tributario</button>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: info.color }}>{info.nombre}</div>
            <h1 className="text-xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Outfit' }} data-testid="decl-cliente">{decl.client_nombre}</h1>
            <p className="text-sm text-slate-500">NIT {decl.nit}{decl.dv ? `-${decl.dv}` : ''} · Año gravable {decl.anio}{decl.periodo ? ` · ${MES_LABEL[decl.periodo] || decl.periodo}` : ''}{decl.tipo_persona ? ` · Persona ${decl.tipo_persona}` : ''}</p>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-xs text-slate-500">Estado
              <select value={decl.estado} onChange={(e) => update({ estado: e.target.value })} className={`block mt-1 h-9 rounded-md border px-2 text-sm ${estadoBadge(decl.estado)}`} data-testid="decl-estado">
                {ESTADOS.map((e) => <option key={e}>{e}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">Responsable
              <select value={decl.responsable_id || ''} onChange={(e) => update({ responsable_id: e.target.value })} className="block mt-1 h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="decl-responsable" disabled={user?.role !== 'Administrador'}>
                {users.length === 0 && <option value={decl.responsable_id || ''}>{decl.responsable_nombre || '—'}</option>}
                {users.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
            </label>
            <Button variant="outline" onClick={() => downloadBlob(`${BACKEND_URL}/api/tributario/plantilla/${decl.formulario}`, info.archivo)} data-testid="decl-descargar-plantilla"><Download className="w-4 h-4 mr-1" /> Plantilla oficial</Button>
          </div>
        </div>
        <textarea defaultValue={decl.notas || ''} onBlur={(e) => e.target.value !== (decl.notas || '') && update({ notas: e.target.value })} placeholder="Notas internas de esta declaración…" className="mt-4 w-full rounded-lg border border-slate-200 p-2.5 text-sm" rows={2} data-testid="decl-notas" />
      </div>

      {decl.formulario === 'f350' && (
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 text-sm font-medium w-fit" data-testid="decl-modo-tabs">
          {[['online', 'Diligenciar en línea'], ['excel', 'Excel diligenciado y soportes']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setModo(k)} className={`rounded-lg px-4 py-1.5 transition-colors ${modo === k ? 'bg-white shadow text-[#1F3864]' : 'text-slate-500 hover:text-slate-800'}`} data-testid={`decl-modo-${k}`}>{l}</button>
          ))}
        </div>
      )}

      {decl.formulario === 'f350' && modo === 'online' ? <Formulario350 declId={declId} decl={decl} /> : (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <section className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4" data-testid="decl-excel-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Excel diligenciado</h2>
            <div className="flex items-center gap-2">
              <input ref={excelRef} type="file" accept=".xlsm,.xlsx" className="hidden" onChange={(e) => subirExcel(e.target.files[0])} data-testid="decl-excel-input" />
              <Button onClick={() => excelRef.current?.click()} disabled={progress !== null} className="bg-[#1F3864] hover:bg-[#162a4d]" data-testid="decl-subir-excel">
                {progress !== null ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Subiendo {progress}%</> : <><Upload className="w-4 h-4 mr-1" /> Subir nueva versión</>}
              </Button>
            </div>
          </div>
          {progress !== null && <div className="h-1.5 w-full rounded bg-slate-100"><div className="h-1.5 rounded bg-[#1F3864] transition-all" style={{ width: `${progress}%` }} /></div>}
          <p className="text-xs text-slate-500">Diligencia la plantilla en Excel (las macros solo funcionan allí), guárdala y súbela aquí. El portal lee los valores calculados, muestra las hojas y genera el PDF del formulario y los anexos.</p>

          {decl.versiones?.length === 0 && <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="decl-sin-versiones">Aún no has subido el Excel diligenciado.</div>}
          {decl.versiones?.length > 0 && (
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {[...decl.versiones].reverse().map((v) => (
                <div key={v.id} className={`flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm ${v.id === versionSel ? 'bg-blue-50/40' : ''}`} data-testid={`decl-version-${v.numero}`}>
                  <button onClick={() => v.parse_status === 'listo' && setVersionSel(v.id)} className="font-medium text-slate-800 hover:underline text-left">v{v.numero} · {v.filename}</button>
                  <span className="text-xs text-slate-500">{formatBytes(v.size)} · {v.uploaded_by} · {formatDateTime(v.uploaded_at)}</span>
                  <span className="flex-1" />
                  {v.parse_status === 'procesando' && <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Procesando hojas…</span>}
                  {v.parse_status === 'listo' && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> {v.sheets.length} hojas</span>}
                  {v.parse_status === 'error' && <span className="inline-flex items-center gap-1 text-xs text-red-700" title={v.parse_error}><AlertTriangle className="w-3.5 h-3.5" /> Error al leer</span>}
                  <button onClick={() => downloadBlob(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/archivos/${v.id}`, v.filename)} className="text-slate-500 hover:text-[#1F3864]" title="Descargar Excel" data-testid={`decl-version-descargar-${v.numero}`}><Download className="w-4 h-4" /></button>
                  {user?.role === 'Administrador' && <button onClick={async () => { if (window.confirm('¿Eliminar esta versión?')) { await axios.delete(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/versiones/${v.id}`); load(); } }} className="text-slate-400 hover:text-red-600" title="Eliminar"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          )}

          {version && (
            <div className="space-y-3 pt-2" data-testid="decl-pdf-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-slate-800">Hojas de v{version.numero} · marca las que irán al PDF</h3>
                <div className="flex gap-2">
                  <button onClick={() => setHojasSel(info.hojas_formulario || [])} className="text-xs text-[#1F3864] hover:underline" data-testid="decl-hojas-formulario">Solo formulario</button>
                  <button onClick={() => setHojasSel(version.sheets.map((s) => s.name))} className="text-xs text-[#1F3864] hover:underline" data-testid="decl-hojas-todas">Todas</button>
                  <button onClick={() => setHojasSel([])} className="text-xs text-slate-500 hover:underline">Ninguna</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {version.sheets.map((s) => {
                  const on = hojasSel.includes(s.name);
                  const isForm = info.hojas_formulario?.includes(s.name);
                  return (
                    <div key={s.name} className={`inline-flex items-center rounded-full border text-xs overflow-hidden ${on ? 'border-[#1F3864] bg-[#1F3864] text-white' : 'border-slate-200 bg-white text-slate-700'}`}>
                      <button onClick={() => setHojasSel(on ? hojasSel.filter((h) => h !== s.name) : [...hojasSel, s.name])} className="px-2.5 py-1" data-testid={`decl-hoja-toggle-${s.name}`}>{isForm ? '★ ' : ''}{s.name}</button>
                      <button onClick={() => verHoja(s.name)} className={`px-1.5 py-1 border-l ${on ? 'border-white/30 hover:bg-white/10' : 'border-slate-200 hover:bg-slate-50'}`} title="Vista previa" data-testid={`decl-hoja-ver-${s.name}`}><Eye className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                })}
              </div>
              <Button onClick={descargarPdf} disabled={pdfLoading || !hojasSel.length} className="bg-emerald-700 hover:bg-emerald-800" data-testid="decl-descargar-pdf">
                {pdfLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generando PDF…</> : <><FileText className="w-4 h-4 mr-1" /> Descargar PDF ({hojasSel.length} hoja{hojasSel.length === 1 ? '' : 's'})</>}
              </Button>
              {preview.hoja && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-slate-600">Vista previa · {preview.hoja}</div>
                  {preview.loading ? <div className="text-xs text-slate-500">Cargando hoja…</div> : <SheetPreview data={preview.data} />}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4" data-testid="decl-docs-section">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Paperclip className="w-5 h-5 text-amber-600" /> Balances y soportes</h2>
          <div className="flex gap-2">
            <select value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)} className="h-9 flex-1 rounded-md border border-slate-200 px-2 text-sm" data-testid="decl-doc-tipo">{TIPOS_DOC.map((t) => <option key={t}>{t}</option>)}</select>
            <input ref={docRef} type="file" multiple className="hidden" onChange={(e) => subirDoc(e.target.files)} data-testid="decl-doc-input" />
            <Button variant="outline" onClick={() => docRef.current?.click()} disabled={docProgress !== null} data-testid="decl-subir-doc">
              {docProgress !== null ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {docProgress}%</> : <><Upload className="w-4 h-4 mr-1" /> Adjuntar</>}
            </Button>
          </div>
          {decl.documentos?.length === 0 && <div className="rounded-lg border border-dashed border-slate-300 p-5 text-center text-xs text-slate-500" data-testid="decl-sin-docs">Sube el balance de prueba, estados financieros, certificados y demás soportes.</div>}
          <ul className="divide-y divide-slate-100">
            {decl.documentos?.map((d) => (
              <li key={d.id} className="flex items-center gap-2 py-2 text-sm" data-testid={`decl-doc-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium text-slate-800">{d.filename}</div>
                  <div className="text-xs text-slate-500">{d.tipo} · {formatBytes(d.size)} · {d.uploaded_by} · {formatDateTime(d.uploaded_at)}</div>
                </div>
                <button onClick={() => downloadBlob(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/archivos/${d.id}`, d.filename)} className="text-slate-500 hover:text-[#1F3864]" title="Descargar"><Download className="w-4 h-4" /></button>
                <button onClick={async () => { if (window.confirm('¿Eliminar este soporte?')) { await axios.delete(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/documentos/${d.id}`); load(); } }} className="text-slate-400 hover:text-red-600" title="Eliminar" data-testid={`decl-doc-eliminar-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </section>
      </div>
      )}
    </div>
  );
}
